import * as React from 'react';

import { Col, Row, Skeleton } from '@rematter/ui-web';
import { LucideFileText, LucideLink, LucidePaperclip } from 'lucide-react';

import type { ChatMessagePart } from '../../_types/messageParts';
import { useTranslations } from '../../i18n/useTranslations';

import { ActionButtons } from './ActionButtons';
import { StreamingMarkdownRenderer } from './StreamingMarkdownRenderer';
import { ThinkingDisplay } from './ThinkingDisplay';

interface PartRendererProps {
  part: ChatMessagePart;
  isUser: boolean;
}

const FadeIn: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => setVisible(true), []);
  return (
    <div
      className="@FadeIn"
      style={{ opacity: visible ? 1 : 0, transition: 'opacity 200ms ease-in' }}
    >
      {children}
    </div>
  );
};

export const PartRenderer = ({ part, isUser }: PartRendererProps) => {
  const t = useTranslations();

  switch (part.data.type) {
    case 'Text': {
      const trimmedText = part.data.text.trim();
      if (!trimmedText) return null;
      if (isUser) {
        return (
          <FadeIn>
            <div className="break-words whitespace-pre-wrap">{trimmedText}</div>
          </FadeIn>
        );
      }
      return <StreamingMarkdownRenderer text={trimmedText} />;
    }

    case 'Reasoning':
      return (
        <FadeIn>
          <ThinkingDisplay thinking={part.data.text} />
        </FadeIn>
      );

    case 'ToolCall': {
      const options = part.data.input?.options;
      if (
        part.data.toolName === 'general-userInput' &&
        Array.isArray(options) &&
        options.length > 0
      ) {
        return (
          <FadeIn>
            <ActionButtons
              options={options as Array<{ label: string; value: string }>}
            />
          </FadeIn>
        );
      }
      if (
        part.data.toolName === 'general-userInput' &&
        part.data.toolCallState === 'InputStreaming'
      ) {
        return (
          <Row gap2 className="mt-2 flex-wrap">
            <Skeleton variant="rounded" width={140} height={36} />
            <Skeleton variant="rounded" width={160} height={36} />
          </Row>
        );
      }
      return null;
    }

    case 'DynamicTool':
      if (part.data.toolCallState === 'OutputError' && part.data.errorText) {
        return (
          <FadeIn>
            <Col
              gap1
              className="rounded border border-failure/50 bg-failure/20 p-2 text-sm text-failure/80"
            >
              <div className="font-medium">
                {t.chat.toolError}: {part.data.toolName}
              </div>
              <div>{part.data.errorText}</div>
            </Col>
          </FadeIn>
        );
      }
      return null;

    case 'File':
      return (
        <FadeIn>
          <Col
            gap1
            className="rounded border border-neutral/20 bg-neutral/5 p-2"
          >
            <Row gap2 className="items-center">
              <Row gap1 className="items-center text-sm font-medium">
                <LucidePaperclip className="size-4" />
                {part.data.filename}
              </Row>
              {part.data.url && (
                <a
                  href={part.data.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline"
                >
                  {t.chat.view}
                </a>
              )}
            </Row>
            <div className="text-xs text-neutral/60">{part.data.mediaType}</div>
          </Col>
        </FadeIn>
      );

    case 'SourceUrl':
      return (
        <FadeIn>
          <div className="rounded border border-neutral/20 bg-neutral/5 p-2">
            <Row gap1 className="items-center text-sm">
              <LucideLink className="size-4" />
              <span className="font-medium">{t.chat.source}:</span>
              <a
                href={part.data.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                {part.data.title || part.data.url}
              </a>
            </Row>
          </div>
        </FadeIn>
      );

    case 'SourceDocument':
      return (
        <FadeIn>
          <Col
            gap1
            className="rounded border border-neutral/20 bg-neutral/5 p-2"
          >
            <Row gap1 className="items-center text-sm">
              <LucideFileText className="size-4" />
              <span className="font-medium">{part.data.title}</span>
              {part.data.filename && (
                <span className="ml-2 text-xs text-neutral/60">
                  ({part.data.filename})
                </span>
              )}
            </Row>
            <div className="text-xs text-neutral/60">{part.data.mediaType}</div>
          </Col>
        </FadeIn>
      );

    case 'Data':
      return (
        <FadeIn>
          <Col
            gap1
            className="rounded border border-neutral/20 bg-neutral/5 p-2"
          >
            <div className="text-xs font-medium text-neutral/60">
              {t.chat.data}: {part.data.dataType}
            </div>
            <pre className="overflow-auto text-xs">
              {JSON.stringify(part.data.data, null, 2)}
            </pre>
          </Col>
        </FadeIn>
      );

    default:
      return null;
  }
};
